import React, {createContext, ReactNode, useCallback, useContext, useRef, useState} from "react";
import {translatorService} from "../services/translator.service";
import {Subscription} from "rxjs";
import {parseError} from "../errors/http.service";
import {useSnackbar} from "./SnackbarContext";

// --- Types ---

interface TextTranslationState {
    translatedText: string;
    isStreaming: boolean;
    sourceLanguage: string;
    targetLanguage: string;
}

interface TextTranslationContextValue extends TextTranslationState {
    setSourceLanguage: (sourceLanguage: string) => void;
    setTargetLanguage: (targetLanguage: string) => void;
    startTranslation: (text: string) => void;
    stopTranslation: () => void;
    clearTranslation: () => void;
}

// --- Initial state ---

const initialState: TextTranslationState = {
    translatedText: '',
    isStreaming: false,
    sourceLanguage: 'pl',
    targetLanguage: 'en',
};

// --- Context ---

const TextTranslationContext = createContext<TextTranslationContextValue | null>(null);

// --- Provider ---

// TODO : fix conversationId type to number
export const TextTranslationProvider = ({ children }: { children: ReactNode }) => {
    const [state, setState] = useState<TextTranslationState>(initialState);
    const [conversationId, setConversationId] = useState<string>('');
    const subscriptionRef = useRef<Subscription | null>(null);
    const { showSnackbar } = useSnackbar();

    const setSourceLanguage = useCallback((sourceLanguage: string) => {
        setState((prev) => ({ ...prev, sourceLanguage }));
    }, []);

    const setTargetLanguage = useCallback((targetLanguage: string) => {
        setState((prev) => ({ ...prev, targetLanguage }));
    }, []);

    const stopTranslation = useCallback(() => {
        if (subscriptionRef.current) {
            subscriptionRef.current.unsubscribe();
            subscriptionRef.current = null;
        }
        setState((prev) => ({ ...prev, isStreaming: false }));
    }, []);

    const clearTranslation = useCallback(() => {

        stopTranslation();
        setState((prev) => ({...prev, translatedText: '', isStreaming: false}));
    }, [stopTranslation]);

    const startTranslation = useCallback(async (text: string) => {
        stopTranslation();

        setState((prev) => ({
            ...prev,
            translatedText: '',
            isStreaming: true,
        }));

        // pobrane z ConversationContext as string but in DB it's a number type
        const id = await translatorService.createTranslationConversation();
        setConversationId(String(id));

        subscriptionRef.current = translatorService
            .createMessageStream({
                conversationId: id,
                text,
                lang: 'pl',
                fromLang: state.sourceLanguage,
                toLang: state.targetLanguage,
            })
            .subscribe({
                next: (chunk) => {
                    setState((prev) => ({
                        ...prev,
                        translatedText: prev.translatedText + chunk
                    }));
                },
                complete: () => {
                    setState((prev) => ({ ...prev, isStreaming: false }));
                    subscriptionRef.current = null;
                },
                error: (error: Error) => {
                    showSnackbar(parseError(error), 'error');
                    setState((prev) => ({ ...prev, isStreaming: false }));
                    subscriptionRef.current = null;
                }
            })
        },
        [state.sourceLanguage, state.targetLanguage, stopTranslation]
    );

    return (
        <TextTranslationContext.Provider
            value={{
                ...state,
                setSourceLanguage,
                setTargetLanguage,
                startTranslation,
                stopTranslation,
                clearTranslation,
            }}
        >
            {children}
        </TextTranslationContext.Provider>
    );
};

// --- Hook ---

export const useTextTranslationContext = (): TextTranslationContextValue => {
    const context = useContext(TextTranslationContext);
    if (!context) {
        throw new Error('useTextTranslation must be used within a TextTranslationProvider');
    }
    return context;
};